import {inject, Injectable} from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import {Content, TDocumentDefinitions} from 'pdfmake/interfaces';
import {UtilService} from './util.service';
import {PdfUtilityService} from './pdf-utility.service';

// Same wiring as the report screens: this build of pdfmake exposes its virtual file system under `pdfMake`.
(<any>pdfMake).vfs = (<any>pdfFonts).pdfMake.vfs;

/**
 * One column of an exported table. `value` returns what the cell should say — already formatted, because a
 * report is read by a person: money as $1,234.56, dates as they appear on screen.
 */
export interface ITableExportColumn<TRow> {
  header: string;
  /** What the cell says in the PDF, and in the CSV unless csvValue overrides it. Already formatted. */
  value: (rowP: TRow) => string;

  /**
   * What the cell says in the CSV, when that differs from the printed form. Money uses this: a spreadsheet
   * cannot sum a column of "$1,203.23" — those are strings, and summing the amounts is the first thing anyone
   * does with an exported ledger. The PDF keeps the dollar signs, because nobody sums a PDF.
   */
  csvValue?: (rowP: TRow) => string;

  /** Right-aligns the column in the PDF. Money and counts read wrong ragged-left. */
  numeric?: boolean;
}

export interface ITableExportRequest<TRow> {
  /** Heading on the PDF and the first line of the CSV: what this table is. */
  title: string;
  /** The scope of the figures — which game, which charity, which window. */
  subtitle?: string;
  columns: ITableExportColumn<TRow>[];
  rows: TRow[];
  /** Label/value lines printed above the table: totals, balances, whatever the screen shows. */
  summary?: {label: string, value: string}[];
  /** Without the extension — both exports add their own. */
  fileName: string;
}

/**
 * Exports any on-screen table as CSV or PDF (#1411: "anytime there is a table of information, have an export
 * by pdf and csv option").
 *
 * A screen describes its table once — columns, rows, and the totals it shows — and gets both formats from it.
 * Six report screens had already hand-rolled a CSV builder each and five had copy-pasted the same pdfmake
 * envelope; the point of this service is that the seventh screen does not, and that the two formats of one
 * table cannot drift apart because they are built from the same description.
 *
 * Escaping and the download itself stay in UtilService, which is where the thousands-separator bug was fixed:
 * a payout of $1,203.23 unquoted splits across two columns and shifts every column after it.
 */
@Injectable({providedIn: 'root'})
export class TableExportService {

  private utilService: UtilService = inject(UtilService);
  private pdfUtilityService: PdfUtilityService = inject(PdfUtilityService);

  /**
   * A byte-order mark, so Excel opens the file as UTF-8 instead of the local codepage. Without it a charity
   * named "Mont-Réal" or an em dash arrives mojibaked, and the export is what a finance person forwards.
   */
  private static readonly UTF8_BOM = '﻿';

  public exportCsv<TRow>(requestP: ITableExportRequest<TRow>): void {
    const lines: string[] = [];

    lines.push(this.utilService.csvRow([requestP.title]));

    if (requestP.subtitle) {
      lines.push(this.utilService.csvRow([requestP.subtitle]));
    }

    if (requestP.summary?.length) {
      // Blank line between sections, as the existing report exports do: one file, two shapes of data, and a
      // reader that would otherwise take the summary's two columns as the table's header.
      lines.push('');

      for (const entry of requestP.summary) {
        lines.push(this.utilService.csvRow([entry.label, entry.value]));
      }
    }

    lines.push('');
    lines.push(this.utilService.csvRow(requestP.columns.map(columnP => columnP.header)));

    for (const row of requestP.rows) {
      lines.push(this.utilService.csvRow(requestP.columns.map(columnP =>
        columnP.csvValue ? columnP.csvValue(row) : columnP.value(row))));
    }

    this.utilService.triggerDownload(TableExportService.UTF8_BOM + lines.join('\r\n'),
      `${requestP.fileName}.csv`);
  }

  /**
   * Downloads rather than opens the PDF. The other reports call pdf.open(), which a pop-up blocker eats and
   * which leaves nothing on disk to attach to an email — and "print page as pdf" is a file, not a tab.
   *
   * Wide tables print landscape: five or more columns squeezed into portrait A4 wraps every heading.
   */
  public exportPdf<TRow>(requestP: ITableExportRequest<TRow>): void {
    const content: Content[] = [
      {text: requestP.title, style: this.pdfUtilityService.PdfStyles.pageTitle, margin: [0, 0, 0, 4]}
    ];

    if (requestP.subtitle) {
      content.push({text: requestP.subtitle, style: this.pdfUtilityService.PdfStyles.subtitle, margin: [0, 0, 0, 8]});
    }

    if (requestP.summary?.length) {
      content.push({
        table: {
          widths: ['auto', 'auto'],
          body: requestP.summary.map(entryP => [
            {text: entryP.label, bold: true, fontSize: 10},
            {text: entryP.value, fontSize: 10}
          ])
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 12]
      });
    }

    content.push({
      table: {
        headerRows: 1,
        // The header row repeats on every page, so page two of a payout history is still readable.
        widths: requestP.columns.map(() => '*'),
        body: [
          requestP.columns.map(columnP => ({
            text: columnP.header,
            bold: true,
            fontSize: 10,
            alignment: columnP.numeric ? 'right' : 'left'
          })),
          ...requestP.rows.map(rowP => requestP.columns.map(columnP => ({
            text: columnP.value(rowP),
            fontSize: 9,
            alignment: columnP.numeric ? 'right' : 'left'
          })))
        ]
      },
      layout: 'lightHorizontalLines'
    });

    if (requestP.rows.length === 0) {
      // An empty table with no word about it reads as a broken export rather than an empty ledger.
      content.push({text: 'No rows to report.', fontSize: 10, italics: true, margin: [0, 8, 0, 0]});
    }

    const document_definition: TDocumentDefinitions = {
      pageOrientation: requestP.columns.length >= 5 ? 'landscape' : 'portrait',
      pageMargins: [40, 40, 40, 60],
      content,
      footer: (currentPageP: number, pageCountP: number) => ({
        text: `Page ${currentPageP} of ${pageCountP}`,
        style: this.pdfUtilityService.PdfStyles.footer
      })
    };

    pdfMake.createPdf(document_definition).download(`${requestP.fileName}.pdf`);
  }

  /**
   * An amount in cents as a plain decimal number for a spreadsheet: 120323 becomes 1203.23, and a chargeback
   * of -125000 becomes -1250.00. No dollar sign and no thousands separator, so the cell arrives as a number
   * Excel can total rather than a string it cannot.
   */
  public csvMoney(centsP: number | null | undefined): string {
    if (centsP === null || centsP === undefined || isNaN(centsP)) {
      return '0.00';
    }

    return (centsP / 100).toFixed(2);
  }

  /**
   * A calendar date as yyyy-MM-dd, which is unambiguous in any locale and sorts correctly as text.
   *
   * Never through DatePipe or toLocaleDateString: the API serializes a payment date as UTC midnight, so a
   * locale conversion renders the previous day for anyone west of UTC — and toLocaleDateString had been
   * printing 10/08/2026, which is either August 10th or October 8th depending on who opens the file.
   */
  public csvDate(valueP: string | Date | null | undefined): string {
    if (!valueP) {
      return '';
    }

    if (typeof valueP === 'string') {
      return valueP.split('T')[0];
    }

    const month = `${valueP.getMonth() + 1}`.padStart(2, '0');
    const day = `${valueP.getDate()}`.padStart(2, '0');

    return `${valueP.getFullYear()}-${month}-${day}`;
  }

  /** A timestamp as yyyy-MM-dd HH:mm, for a column that records when something was entered. */
  public csvDateTime(valueP: string | Date | null | undefined): string {
    if (!valueP) {
      return '';
    }

    const value = typeof valueP === 'string' ? new Date(valueP) : valueP;

    if (isNaN(value.getTime())) {
      return '';
    }

    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');

    return `${this.csvDate(value)} ${hours}:${minutes}`;
  }

  /**
   * A filename stem that says what the file is and when it was taken: two exports of the same table on
   * different days must not land on top of each other in a downloads folder.
   */
  public fileNameFor(partsP: (string | null | undefined)[], onP: Date = new Date()): string {
    const month = `${onP.getMonth() + 1}`.padStart(2, '0');
    const day = `${onP.getDate()}`.padStart(2, '0');

    const stem = partsP
      .filter((partP): partP is string => !!partP && partP.trim().length > 0)
      .map(partP => partP.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))
      .filter(partP => partP.length > 0)
      .join('-');

    return `${stem || 'export'}-${onP.getFullYear()}-${month}-${day}`;
  }
}
